import { useCallback } from 'react';
import type { ZoomLevel, AggregatedDataPoint } from '../types';
import { CONFIG } from '../constants';

export const useEventHandlers = (
    panningState: any,
    aggregatedData: AggregatedDataPoint[],
    zoomLevel: ZoomLevel,
    isExpanded: boolean
) => {
    const getWindowSize = useCallback(() => {
        const windowSizes = isExpanded ? CONFIG.EXPANDED_WINDOW_SIZES : CONFIG.WINDOW_SIZES;
        return windowSizes[zoomLevel];
    }, [zoomLevel, isExpanded]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        const windowSize = getWindowSize();
        if (aggregatedData.length <= windowSize) return;

        e.preventDefault();
        panningState.setIsDragging(true);
        panningState.dragStartX.current = e.clientX;
        panningState.dragStartOffset.current = panningState.panOffset;
        panningState.setTempOffset(null);
    }, [panningState, aggregatedData.length, getWindowSize]);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        const windowSize = getWindowSize();
        if (!panningState.isDragging || aggregatedData.length <= windowSize) return;

        e.preventDefault();
        const deltaX = e.clientX - panningState.dragStartX.current;
        const pixelsPerItem = CONFIG.WIDTH_ESTIMATE / windowSize;
        const itemsDelta = -deltaX / pixelsPerItem;
        const newOffset = panningState.dragStartOffset.current + itemsDelta;
        const maxOffset = Math.max(0, aggregatedData.length - windowSize);
        const clampedOffset = Math.max(0, Math.min(maxOffset, newOffset));

        const roundedOffset = Math.round(clampedOffset * 10) / 10;
        if (panningState.tempOffset === null || Math.abs(roundedOffset - (panningState.tempOffset || 0)) >= CONFIG.DRAG_SENSITIVITY) {
            panningState.setTempOffset(roundedOffset);
        }
    }, [panningState, aggregatedData.length, getWindowSize]);

    const handleMouseUp = useCallback(() => {
        if (panningState.tempOffset !== null) {
            panningState.setPanOffset(Math.round(panningState.tempOffset));
        }
        panningState.setIsDragging(false);
        panningState.setTempOffset(null);
    }, [panningState]);

    const handleMouseMoveTracking = useCallback((e: React.MouseEvent) => {
        panningState.setCursorPosition({ x: e.clientX, y: e.clientY });
    }, [panningState]);

    return {
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        handleMouseMoveTracking,
    };
};
